import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { User } from 'src/app/shared/interfaces/interfaces';
import { AppService } from 'src/app/shared/services/app.service';
import { AdminService } from 'src/app/admin/services/admin.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DeletionDialogComponent } from 'src/app/shared/dialogs/deletion-dialog/deletion-dialog.component';
import * as moment from 'moment';
import { trigger } from '@angular/animations';
import { fadeIn } from 'src/app/shared/animations/animations';


@Component({
  selector: 'app-user-pending-list',
  templateUrl: './user-pending-list.component.html',
  styleUrls: ['./user-pending-list.component.scss'],
  animations: [trigger('fadeIn', fadeIn())]
})
export class UserPendingListComponent implements OnInit, OnDestroy {

  dataSource = new MatTableDataSource<User>();
  selection = new SelectionModel<User>(false, []);
  displayedColumns = ['select', 'name', 'surname', 'birthday', 'address', 'date_created', 'status', 'registered_by', 'delete'];

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  pageSizeOptions = [10, 25, 50, 100];
  pageSize = 10;
  currentPage = 1;
  totalResults = 0;
  isLoading = false;

  private destroy$ = new Subject<void>();

  constructor(
    private appService: AppService,
    private adminService: AdminService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.setupPaginator();
    this.setupSelectionTracking();
    this.loadUsers();
  }

  private setupPaginator(): void {
    this.paginator.pageSizeOptions = this.pageSizeOptions;
    this.paginator.pageSize = this.pageSize;

    this.paginator.page
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: PageEvent) => {
        this.currentPage = event.pageIndex + 1;
        this.pageSize = event.pageSize;
        this.loadUsers();
      });
  }

  private setupSelectionTracking(): void {
    this.selection.changed
      .pipe(takeUntil(this.destroy$))
      .subscribe(selection => {
        this.adminService.selectedUser$.next(
          selection.added.length ? selection.added[0] : undefined
        );
      });
  }

  private loadUsers(): void {
    this.isLoading = true;

    const payload = {
      sort: 1,
      page: this.currentPage,
      limit: this.pageSize,
      activity_problem: [0, 1],
      disabilities: [],
      lowest_age: 0,
      highest_age: 140,
      statuses: ['pending']
    };

    this.appService.fetch_manager_users_with_filters(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.processUserData(response.data);
          this.totalResults = response.total_results;
          this.currentPage = response.current_page;
          this.pageSize = response.limit;
          this.paginator.length = this.totalResults;
          this.paginator.pageIndex = this.currentPage - 1;
          this.isLoading = false;
        },
        (error) => {
          console.error('Error loading users:', error);
          this.isLoading = false;
        }
      );
  }

  private processUserData(data: User[]): void {
    data.forEach(user => {
      user['age'] = moment().diff(new Date(user.birthday), 'years');
    });

    this.dataSource.data = data;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property): string | number => {
      switch (property) {
        case 'date_created':
          return new Date(item.created).getTime();
        case 'date_updated':
          return new Date(item.updated).getTime();
        default:
          return item[property];
      }
    };
  }

  deleteRow(row: User, event: Event): void {
    event.stopPropagation();

    const dialogRef = this.dialog.open(DeletionDialogComponent, {
      data: {
        user: row,
        title: 'Διαγραφή χρήστη',
        message: 'Είστε σίγουροι ότι επιθυμείτε να διαγράψετε τον χρήστη;'
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result === 'confirmed') {
          this.appService.delete_user({ id: [row._id] })
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              () => this.loadUsers(),
              (error) => console.error('Error deleting user:', error)
            );
        }
      });
  }

  exportToPdf(row: User, event: Event): void {
    event.stopPropagation();

    this.appService.exportToPdf(row._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: Blob) => {
          const fileURL = URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
          window.open(fileURL, '_blank');
        },
        (error) => console.error('Error downloading PDF:', error)
      );
  }

  exportToExcel(): void {
    const payload = {
      sort: 1,
      page: this.currentPage,
      limit: this.pageSize,
      activity_problem: [0, 1],
      disabilities: [],
      lowest_age: 0,
      highest_age: 140,
      statuses: ['pending'],
      excelFile: 1
    };

    this.appService.exportToExcel(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (blob) => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'users.xlsx';
          link.click();
        },
        (error) => console.error('Error exporting to Excel:', error)
      );
  }

  toggleRowSelection(row: User, event?: Event): void {
    if (event) {
      event.stopPropagation();
      const isDeleteButton = (event.target as HTMLElement).classList.contains('delete-button');
      if (isDeleteButton) return;
    }
    this.selection.toggle(row);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.adminService.selectedUser$.next(undefined);
  }
}